import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Loader2, ArrowLeft, Sparkles, Cpu, BarChart3, Compass, Settings, ShieldAlert,
  Brain, PenTool, Code, DollarSign, Users, Megaphone, TrendingUp, Truck, HeartPulse,
  Scale, GraduationCap, Rocket, Search,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { loadContent, isLocked } from '../lib/contentLoader';
import useCanSeeCounts from '../hooks/useCanSeeCounts';
import ContentLockedCard from '../components/ContentLockedCard';

// Department-specific icons + colors
const DEPT_VISUALS = {
  'AI Engineering & Development': { icon: Cpu, dot: 'bg-teal-500', text: 'text-teal-700', bg: 'bg-teal-50', solid: 'bg-teal-600' },
  'Data & Analytics': { icon: BarChart3, dot: 'bg-violet-500', text: 'text-violet-700', bg: 'bg-violet-50', solid: 'bg-violet-600' },
  'AI Product & Strategy': { icon: Compass, dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', solid: 'bg-blue-600' },
  'AI Operations & Automation': { icon: Settings, dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', solid: 'bg-amber-600' },
  'AI Governance, Risk & Security': { icon: ShieldAlert, dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', solid: 'bg-red-600' },
  'AI Training & Evaluation': { icon: Brain, dot: 'bg-purple-500', text: 'text-purple-700', bg: 'bg-purple-50', solid: 'bg-purple-600' },
  'AI Content, Marketing & Design': { icon: PenTool, dot: 'bg-pink-500', text: 'text-pink-700', bg: 'bg-pink-50', solid: 'bg-pink-600' },
  'Software & IT (AI-Integrated)': { icon: Code, dot: 'bg-sky-500', text: 'text-sky-700', bg: 'bg-sky-50', solid: 'bg-sky-600' },
  'Finance (AI-Integrated)': { icon: DollarSign, dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', solid: 'bg-emerald-600' },
  'HR & Talent (AI-Integrated)': { icon: Users, dot: 'bg-blue-500', text: 'text-blue-700', bg: 'bg-blue-50', solid: 'bg-blue-600' },
  'Marketing (AI-Integrated)': { icon: Megaphone, dot: 'bg-rose-500', text: 'text-rose-700', bg: 'bg-rose-50', solid: 'bg-rose-600' },
  'Sales (AI-Integrated)': { icon: TrendingUp, dot: 'bg-orange-500', text: 'text-orange-700', bg: 'bg-orange-50', solid: 'bg-orange-600' },
  'Operations & Supply Chain (AI-Integrated)': { icon: Truck, dot: 'bg-yellow-500', text: 'text-yellow-700', bg: 'bg-yellow-50', solid: 'bg-yellow-600' },
  'Healthcare (AI-Integrated)': { icon: HeartPulse, dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', solid: 'bg-red-600' },
  'Legal (AI-Integrated)': { icon: Scale, dot: 'bg-stone-500', text: 'text-stone-700', bg: 'bg-stone-50', solid: 'bg-stone-600' },
  'Education (AI-Integrated)': { icon: GraduationCap, dot: 'bg-indigo-500', text: 'text-indigo-700', bg: 'bg-indigo-50', solid: 'bg-indigo-600' },
  'Entrepreneurship & Consulting': { icon: Rocket, dot: 'bg-purple-500', text: 'text-purple-700', bg: 'bg-purple-50', solid: 'bg-purple-600' },
};
const DEFAULT_VIS = { icon: Brain, dot: 'bg-gray-500', text: 'text-gray-700', bg: 'bg-gray-50', solid: 'bg-gray-600' };
const getVis = (name) => DEPT_VISUALS[name] || DEFAULT_VIS;

const AIPathwayPage = () => {
  const canSeeCounts = useCanSeeCounts();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    loadContent('ai_careers.json')
      .then((d) => {
        if (isLocked(d)) { setLocked(true); setLoading(false); return; }
        if (d) setPkg(d);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { setQuery(''); window.scrollTo(0, 0); }, [selectedDept]);

  const dept = useMemo(() => pkg?.departments.find(d => (d.name || d.department) === selectedDept), [pkg, selectedDept]);

  const filteredRoles = useMemo(() => {
    if (!dept) return [];
    const q = query.trim().toLowerCase();
    if (!q) return dept.roles;
    return dept.roles.filter(r =>
      r.title.toLowerCase().includes(q) ||
      r.summary?.toLowerCase().includes(q)
    );
  }, [dept, query]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50"><Loader2 className="animate-spin text-teal-600" size={32} /></div>;
  }

  if (locked) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-32 px-4"><ContentLockedCard title="AI Pathway isn't open right now" /></div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ai-pathway-page">
      <Navbar />

      {/* ─── Initial state: dept tiles + quiz CTA ─── */}
      {!selectedDept && (
        <>
          <section className="pt-28 pb-6 px-4 md:px-6 bg-gradient-to-br from-teal-50 via-white to-blue-50">
            <div className="max-w-5xl mx-auto text-center">
              <Badge className="bg-teal-100 text-teal-700 mb-3 font-semibold">AI Pathway · Cross-domain AI Careers</Badge>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3">Find Your AI Career</h1>
              <p className="text-gray-600 max-w-2xl mx-auto text-sm md:text-base mb-5">
                Pick a department to see only its AI-powered roles — each with readiness score, career journey, skills, risks, and earning paths. Or take the universal Career Fit Quiz to get matched.
              </p>
              <Button asChild size="lg" data-testid="career-quiz-cta" className="bg-teal-600 hover:bg-teal-700 text-white shadow-md">
                <Link to="/explore/ai/quiz">
                  <Sparkles size={16} className="mr-2" /> Take the Career Fit Quiz
                </Link>
              </Button>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10">
            <div className="max-w-6xl mx-auto">
              {canSeeCounts && <p className="text-xs text-gray-500 mb-4">Showing <strong>{pkg.departments.length}</strong> departments · <strong>{pkg.total_roles}</strong> roles total</p>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dept-grid">
                {pkg.departments.map(d => {
                  const name = d.name || d.department;
                  const v = getVis(name);
                  const Icon = v.icon;
                  return (
                    <button
                      key={name}
                      data-testid={`dept-tile-${d.roles[0].id}`}
                      onClick={() => setSelectedDept(name)}
                      className="group relative text-left p-5 bg-white rounded-2xl border-2 border-gray-200 hover:border-gray-400 hover:shadow-lg transition-all overflow-hidden"
                    >
                      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full ${v.bg} opacity-50 group-hover:opacity-80 transition-opacity`} />
                      <div className={`relative inline-flex items-center justify-center w-12 h-12 rounded-xl ${v.bg} ${v.text} mb-3`}>
                        <Icon size={22} />
                      </div>
                      <h3 className="relative text-base md:text-lg font-bold text-gray-900 mb-3 pr-6 leading-snug">{name}</h3>
                      <div className="relative flex items-center justify-between">
                        <Badge className={`${v.bg} ${v.text} border-0 text-[11px] font-bold`}>{canSeeCounts ? `${d.roles.length} roles` : 'Explore'}</Badge>
                        <ArrowRight size={16} className="text-gray-400 group-hover:text-gray-700 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        </>
      )}

      {/* ─── Dept detail view: only that dept's roles ─── */}
      {selectedDept && dept && (
        <>
          <section className="pt-28 pb-10 px-4 md:px-6 bg-gradient-to-br from-teal-600 via-teal-700 to-blue-700 text-white">
            <div className="max-w-5xl mx-auto">
              <button onClick={() => setSelectedDept(null)} data-testid="back-to-depts" className="inline-flex items-center gap-1.5 text-sm text-white/90 hover:text-white mb-4 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full">
                <ArrowLeft size={14} /> Back to all departments
              </button>
              <Badge className="bg-white text-teal-700 border-0 font-bold mb-2">AI Pathway</Badge>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{selectedDept}</h1>
              <p className="text-white/85 text-sm md:text-base">{canSeeCounts ? `${dept.roles.length} AI-powered roles inside this department` : 'AI-powered roles inside this department'}</p>
            </div>
          </section>

          {/* Search */}
          <section className="px-4 md:px-6 py-6 bg-white border-b border-gray-200">
            <div className="max-w-5xl mx-auto relative max-w-xl">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                data-testid="dept-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={canSeeCounts ? `Search ${dept.roles.length} roles in ${selectedDept}...` : `Search roles in ${selectedDept}...`}
                className="pl-9"
              />
            </div>
          </section>

          <section className="px-4 md:px-6 py-10">
            <div className="max-w-5xl mx-auto">
              <div className="grid md:grid-cols-2 gap-4" data-testid="roles-grid">
                {filteredRoles.map((r, idx) => <RoleCard key={r.id} role={r} visual={getVis(selectedDept)} index={idx + 1} />)}
              </div>
              {filteredRoles.length === 0 && <p className="text-center text-sm text-gray-500 py-8">No roles match your search.</p>}
            </div>
          </section>
        </>
      )}

      {/* How to read this library — methodology & disclaimers */}
      {pkg?.library && (
        <section className="px-4 md:px-6 py-10 bg-white border-t border-gray-200" data-testid="ai-library-notes">
          <div className="max-w-5xl mx-auto space-y-4">
            <h2 className="text-lg md:text-xl font-bold text-gray-900">How to read this library</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {[
                ['How to read this library', pkg.library.howToReadThisLibrary],
                ['How ratings are assigned', pkg.library.howRatingsAreAssigned],
                ['Market information review', pkg.library.marketInformationReview],
              ].map(([label, items]) => (
                Array.isArray(items) && items.length > 0 && (
                  <div key={label} className="p-5 bg-gray-50 rounded-2xl border border-gray-200">
                    <p className="text-[11px] uppercase font-bold tracking-widest text-gray-500 mb-2">{label}</p>
                    <div className="space-y-2">
                      {items.map((t, i) => (
                        <p key={i} className="text-xs text-gray-600 leading-relaxed">{t}</p>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

const TIER_W = { Foundation: 25, Developing: 50, Strong: 75, Advanced: 100 };

const RoleCard = ({ role, visual, index }) => {
  const rs = role.readinessScore?.overall;
  return (
    <Link
      to={`/explore/ai/role/${role.id}`}
      data-testid={`role-card-${role.id}`}
      className="group p-5 bg-white rounded-2xl border-2 border-gray-200 hover:border-gray-400 hover:shadow-lg transition-all flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <span className={`flex-shrink-0 w-9 h-9 rounded-lg ${visual.bg} ${visual.text} flex items-center justify-center text-sm font-bold`}>{String(index).padStart(2, '0')}</span>
        <div className="flex-1 min-w-0">
          <h3 className="text-base md:text-lg font-bold text-gray-900 leading-tight">{role.title}</h3>
          {role.summary && <p className="text-xs text-gray-600 mt-1.5 line-clamp-3">{role.summary}</p>}
        </div>
      </div>
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        {rs !== undefined && (
          <div className="flex items-center gap-1.5">
            <div className={`text-xs font-bold ${visual.text}`}>{typeof rs === 'number' ? `Readiness ${rs}%` : `Readiness · ${rs}`}</div>
            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full ${visual.solid}`} style={{ width: `${typeof rs === 'number' ? rs : (TIER_W[rs] || 0)}%` }} />
            </div>
          </div>
        )}
        <ArrowRight size={14} className={`${visual.text} group-hover:translate-x-0.5 transition-transform`} />
      </div>
    </Link>
  );
};

// Export helper so the role detail page can reuse the visual map
export { DEPT_VISUALS, getVis };
export default AIPathwayPage;
